import { API_CONFIG } from "../config/api";
import { ApiError, getApiClientOptions } from "./client";

interface GraphQLResponse<T> {
	data?: T;
	errors?: Array<{
		message: string;
		locations?: Array<{ line: number; column: number }>;
		path?: string[];
	}>;
}

export async function graphqlRequest<T>(
	query: string,
	variables?: Record<string, unknown>,
): Promise<T> {
	const url = API_CONFIG.graphqlBaseUrl || `${API_CONFIG.baseUrl}/graphql`;
	const options = getApiClientOptions();

	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};

	if (options.instanceId) {
		headers["x-instance-id"] = options.instanceId;
	}

	if (options.getAccessToken) {
		try {
			const token = await Promise.race([
				options.getAccessToken(),
				new Promise<null>((_, reject) =>
					setTimeout(() => reject(new Error("Token acquisition timed out")), 10_000),
				),
			]);
			if (token) {
				headers.Authorization = `Bearer ${token}`;
			}
		} catch (error) {
			console.warn("Failed to get access token for GraphQL:", error);
		}
	}

	const response = await fetch(url, {
		method: "POST",
		headers,
		body: JSON.stringify({ query, variables }),
		signal: AbortSignal.timeout(10_000),
	});

	if (!response.ok) {
		let detail = response.statusText;
		try {
			const body = await response.text();
			if (body) {
				console.error("GraphQL error response:", body);
				detail = body;
			}
		} catch {
			// ignore – use statusText
		}
		throw new ApiError(detail, response.status, response.statusText);
	}

	const result: GraphQLResponse<T> = await response.json();

	if (result.errors?.length) {
		const message = result.errors.map((e) => e.message).join("; ");
		throw new ApiError(message, 200, "GraphQL Error");
	}

	if (!result.data) {
		throw new ApiError("No data returned from GraphQL", 200, "Empty Response");
	}

	return result.data;
}
